import { ref, computed, watch } from 'vue'
import type { Map as LeafletMap, LayerGroup as LeafletLayerGroup, Marker as LeafletMarker, Polyline as LeafletPolyline, DivIcon as LeafletDivIcon } from 'leaflet'
import type { Incident, Supervisor, Agency, Route } from './useMapData'

// Leaflet dinámico: será inyectado desde las vistas que lo cargan con import()
let L: typeof import('leaflet') | null = null
export function setLeafletModule(mod: typeof import('leaflet')) { L = mod }

// MarkerCluster dinámico: se cargará sólo cuando se active el clustering
let MarkerClusterGroupRef: any | null = null
async function ensureMarkerClusterLoaded() {
  if (!MarkerClusterGroupRef) {
    const mod = await import('leaflet.markercluster')
    // Cargar CSS del plugin de forma diferida
    await import('leaflet.markercluster/dist/MarkerCluster.css')
    await import('leaflet.markercluster/dist/MarkerCluster.Default.css')
    MarkerClusterGroupRef = (mod as any).MarkerClusterGroup
  }
}

// Helper: mapa simple de iconos a emojis para no depender de @mdi/font
function iconToEmoji(name: string): string {
  switch (name) {
    case 'alert':
    case 'alert-circle':
    case 'alert-circle-outline':
      return '⚠️'
    case 'alert-octagon':
      return '⛔'
    case 'account-supervisor':
      return '🧑‍💼'
    case 'account-off':
      return '🚫'
    case 'account-arrow-right':
      return '➡️'
    case 'account-check':
    case 'check-circle':
      return '✅'
    case 'store':
      return '🏪'
    case 'store-off':
      return '🚫'
    case 'store-cog':
      return '🛠️'
    case 'routes':
      return '🛣️'
    case 'map-marker':
      return '📍'
    case 'close-circle':
      return '❌'
    case 'flag':
      return '🚩'
    case 'flag-checkered':
      return '🏁'
    default:
      return '•'
  }
}

// Configuración de iconos (DivIcon personalizados)
const createCustomIcon = (
  iconName: string, 
  color: string, 
  size: 'small' | 'medium' | 'large' = 'medium'
) => {
  const sizes = {
    small: [20, 20],
    medium: [30, 30],
    large: [40, 40]
  }
  
  const emoji = iconToEmoji(iconName)
  // Usar box-sizing: border-box para que el borde no altere el tamaño visual
  // y anclar exactamente al centro con valores enteros para evitar drift por redondeo
  const [w, h] = sizes[size] as [number, number]
  return new L!.DivIcon({
    html: `
      <div style="
        background-color: ${color};
        border: 2px solid white;
        box-sizing: border-box;
        border-radius: 50%;
        width: ${w}px;
        height: ${h}px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        font-size: ${size === 'small' ? '10px' : size === 'medium' ? '14px' : '18px'};
        color: white;
      ">
        <span aria-hidden="true">${emoji}</span>
      </div>
    `,
    className: 'custom-map-icon',
    iconSize: [w, h],
    iconAnchor: [Math.round(w / 2), Math.round(h / 2)]
  })
}

// Configuración de colores y iconos por tipo (Sistema de colores MGI)
const INCIDENT_COLORS = {
  'BAJA': '#4CAF50',      // Verde - Baja prioridad
  'MEDIA': '#FF9800',     // Naranja - Media prioridad
  'ALTA': '#FF5722',      // Rojo-naranja - Alta prioridad
  'CRITICA': '#D32F2F'    // Rojo intenso - Crítica
}

const INCIDENT_ICONS = {
  'BAJA': 'alert',
  'MEDIA': 'alert-circle',
  'ALTA': 'alert-octagon',
  'CRITICA': 'alert-circle-outline'
}

const SUPERVISOR_COLORS = {
  'ACTIVO': '#2196F3',      // Azul - En servicio activo
  'INACTIVO': '#9E9E9E',    // Gris - Fuera de servicio
  'EN_RUTA': '#9C27B0',     // Púrpura - En ruta asignada
  'DISPONIBLE': '#4CAF50'   // Verde - Disponible para asignación
}

const SUPERVISOR_ICONS = {
  'ACTIVO': 'account-supervisor',
  'INACTIVO': 'account-off',
  'EN_RUTA': 'account-arrow-right',
  'DISPONIBLE': 'account-check'
}

const AGENCY_COLORS = {
  'ACTIVA': '#4CAF50',        // Verde - Operativa
  'INACTIVA': '#9E9E9E',      // Gris - No operativa
  'MANTENIMIENTO': '#FF9800'  // Naranja - En mantenimiento
}

const AGENCY_ICONS = {
  'ACTIVA': 'store',
  'INACTIVA': 'store-off',
  'MANTENIMIENTO': 'store-cog'
}

const ROUTE_COLORS = {
  'ACTIVA': '#2196F3',
  'PAUSADA': '#FF9800',
  'COMPLETADA': '#4CAF50',
  'CANCELADA': '#F44336',
  'PLANIFICADA': '#9C27B0'
} as const

const ROUTE_STYLES = {
  'ACTIVA': { weight: 5, opacity: 0.8, dashArray: undefined },
  'PAUSADA': { weight: 4, opacity: 0.6, dashArray: '10, 8' },
  'COMPLETADA': { weight: 3, opacity: 0.5, dashArray: undefined },
  'CANCELADA': { weight: 3, opacity: 0.4, dashArray: '5, 5' },
  'PLANIFICADA': { weight: 4, opacity: 0.7, dashArray: '15, 5' }
} as const

export interface LayerVisibility {
  incidents: boolean
  supervisors: boolean
  agencies: boolean
  routes: boolean
  clustering: boolean
  heatmap: boolean
}

export interface LayerFilters {
  incidents: {
    priorities: string[]
    statuses: string[]
    categories: string[]
  }
  supervisors: {
    statuses: string[]
    zones: string[]
  }
  agencies: {
    statuses: string[]
    zones: string[]
  }
  routes: {
    statuses: string[]
  }
}

export function useMapLayers(
  map: LeafletMap | null,
  opts?: { popupMode?: 'full' | 'compact'; enablePopups?: boolean }
) {
  const isCompactPopup = opts?.popupMode === 'compact'
  const enablePopups = opts?.enablePopups !== false
  // Estados reactivos
  const layerVisibility = ref<LayerVisibility>({
    incidents: true,
    supervisors: true,
    agencies: true,
    routes: false,
    clustering: false, // Desactivar clustering por defecto para evitar conflictos
    heatmap: false
  })

  const layerFilters = ref<LayerFilters>({
    incidents: {
      priorities: ['ALTA', 'CRITICA'],
      statuses: ['ABIERTA', 'EN_PROGRESO'],
      categories: []
    },
    supervisors: {
      statuses: ['ACTIVO', 'EN_RUTA'],
      zones: []
    },
    agencies: {
      statuses: ['ACTIVA'],
      zones: []
    },
    routes: {
      statuses: ['ACTIVA']
    }
  })

  // Grupos de capas
  const incidentLayers = ref<any | null>(null)
  const supervisorLayers = ref<any | null>(null)
  const agencyLayers = ref<any | null>(null)
  const routeLayers = ref<any | null>(null)

  // Inicializar grupos de capas
  const initializeLayers = () => {
    if (!map) return

    // Limpiar capas existentes
    clearAllLayers()

    // Crear grupos de capas
    if (layerVisibility.value.clustering) {
      // Si el plugin aún no está cargado, usar una LayerGroup temporal y luego
      // el watcher de clustering hará el reemplazo al finalizar la carga.
      if (!MarkerClusterGroupRef) {
        incidentLayers.value = new L!.LayerGroup()
      } else {
        incidentLayers.value = new MarkerClusterGroupRef({
        chunkedLoading: true,
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true,
          iconCreateFunction: (cluster: any) => {
          const count = cluster.getChildCount()
          let size = 'medium'
          let color = '#2196F3'
          
          if (count > 20) {
            size = 'large'
            color = '#F44336'
          } else if (count > 10) {
            size = 'medium'
            color = '#FF9800'
          }
          
          return new L!.DivIcon({
            html: `
              <div style="
                background-color: ${color};
                border: 3px solid white;
                border-radius: 50%;
                width: ${size === 'large' ? '50px' : '40px'};
                height: ${size === 'large' ? '50px' : '40px'};
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 3px 8px rgba(0,0,0,0.4);
                font-weight: bold;
                color: white;
                font-size: ${size === 'large' ? '16px' : '14px'};
              ">
                ${count}
              </div>
            `,
            className: 'cluster-icon',
            iconSize: [size === 'large' ? 50 : 40, size === 'large' ? 50 : 40]
          })
        }
        })
      }
    } else {
      incidentLayers.value = new L!.LayerGroup()
    }

    supervisorLayers.value = new L!.LayerGroup()
    agencyLayers.value = new L!.LayerGroup()
    routeLayers.value = new L!.LayerGroup()

    // Agregar capas al mapa si están visibles
    if (layerVisibility.value.incidents && incidentLayers.value) {
      map.addLayer(incidentLayers.value)
    }
    if (layerVisibility.value.supervisors && supervisorLayers.value) {
      map.addLayer(supervisorLayers.value)
    }
    if (layerVisibility.value.agencies && agencyLayers.value) {
      map.addLayer(agencyLayers.value)
    }
    if (layerVisibility.value.routes && routeLayers.value) {
      map.addLayer(routeLayers.value)
    }
  }

  // Limpiar todas las capas
  const clearAllLayers = () => {
    if (!map) return

    if (incidentLayers.value) {
      map.removeLayer(incidentLayers.value)
      incidentLayers.value.clearLayers()
    }
    if (supervisorLayers.value) {
      map.removeLayer(supervisorLayers.value)
      supervisorLayers.value.clearLayers()
    }
    if (agencyLayers.value) {
      map.removeLayer(agencyLayers.value)
      agencyLayers.value.clearLayers()
    }
    if (routeLayers.value) {
      map.removeLayer(routeLayers.value)
      routeLayers.value.clearLayers()
    }
  }

  // Filtrar y mostrar incidencias con capas temáticas avanzadas
  const updateIncidentLayer = (incidents: Incident[]) => {
    if (!incidentLayers.value) return

    incidentLayers.value.clearLayers()

    const filteredIncidents = incidents.filter(incident => {
      const priorityMatch = layerFilters.value.incidents.priorities.length === 0 ||
        layerFilters.value.incidents.priorities.includes(incident.priority)
      
      const statusMatch = layerFilters.value.incidents.statuses.length === 0 ||
        layerFilters.value.incidents.statuses.includes(incident.status)
      
      const categoryMatch = layerFilters.value.incidents.categories.length === 0 ||
        layerFilters.value.incidents.categories.includes(incident.category)

      return priorityMatch && statusMatch && categoryMatch
    })

    filteredIncidents.forEach(incident => {
      // Validar que las coordenadas existan y sean válidas
      if (!incident.latitude || !incident.longitude || 
          isNaN(incident.latitude) || isNaN(incident.longitude)) {
        console.warn('⚠️ Incidencia sin coordenadas válidas:', incident.id, incident.title)
        return
      }

      const icon = createCustomIcon(
        INCIDENT_ICONS[incident.priority],
        INCIDENT_COLORS[incident.priority],
        incident.priority === 'CRITICA' ? 'large' : incident.priority === 'ALTA' ? 'medium' : 'small'
      )

  const marker = new L!.Marker([incident.latitude, incident.longitude], { icon, interactive: enablePopups })
      
      // Determinar estado visual
      const statusColor = incident.status === 'ABIERTA' ? '#f44336' : 
                         incident.status === 'EN_PROGRESO' ? '#ff9800' :
                         incident.status === 'RESUELTA' ? '#4caf50' : '#9e9e9e'
      
      const urgencyIndicator = incident.priority === 'CRITICA' ? 
        '<div style="background: linear-gradient(45deg, #ff0000, #ff6b6b); color: white; padding: 2px 6px; border-radius: 12px; font-size: 10px; font-weight: bold; text-align: center; margin: 4px 0; animation: pulse 2s infinite;">🚨 CRÍTICA 🚨</div>' : ''
      
      const popupContent = `
  <div class="mgi-popup ${isCompactPopup ? 'mgi-popup--compact' : ''}" style="font-family: 'Roboto', sans-serif;">
          <div style="
            background: linear-gradient(135deg, ${INCIDENT_COLORS[incident.priority]}, ${INCIDENT_COLORS[incident.priority]}dd);
            color: white;
            padding: 8px;
            margin: -8px -8px 8px -8px;
            border-radius: 8px 8px 0 0;
          ">
            <h4 style="margin: 0; font-size: 1rem; display: flex; align-items: center;">
              <span style="margin-right: 8px; font-size: 1.2rem;">${iconToEmoji(INCIDENT_ICONS[incident.priority])}</span>
              ${incident.title}
            </h4>
            <div style="display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap;">
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold;">
                ${incident.priority}
              </span>
              <span style="background: ${statusColor}; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold;">
                ${incident.status}
              </span>
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                ${incident.category}
              </span>
            </div>
          </div>
          
          ${urgencyIndicator}
          
          <div style="margin: 12px 0;">
            <p style="margin: 8px 0; color: #555; font-size: 14px; line-height: 1.4;">
              <strong>Descripción:</strong><br>
              ${incident.description}
            </p>
          </div>
          
          <div style="background: #f8f9fa; padding: 12px; border-radius: 6px; margin: 12px 0;">
            <div style="display: grid; grid-template-columns: 1fr; gap: 6px; font-size: 11px;">
              <div><strong>📅 Creado:</strong><br>${new Date(incident.createdAt).toLocaleString('es-DO')}</div>
              <div><strong>🔄 Actualizado:</strong><br>${new Date(incident.updatedAt).toLocaleString('es-DO')}</div>
              <div><strong>📊 Estado Actual:</strong><br>
                <span style="
                  background: ${statusColor}; 
                  color: white; 
                  padding: 2px 8px; 
                  border-radius: 12px; 
                  font-weight: bold; 
                  font-size: 11px;
                ">
                  ${incident.status === 'ABIERTA' ? '🔴 ABIERTA' :
                    incident.status === 'EN_PROGRESO' ? '🟡 EN PROGRESO' :
                    incident.status === 'RESUELTA' ? '🟢 RESUELTA' : '⚪ ' + incident.status}
                </span>
              </div>
              ${incident.assignedSupervisor ? `<div><strong>👤 Supervisor:</strong><br>${incident.assignedSupervisor}</div>` : '<div><strong>👤 Supervisor:</strong><br><span style="color: #f44336;">Sin asignar</span></div>'}
              ${incident.affectedAgencies?.length ? `<div><strong>🏢 Agencias:</strong><br>${incident.affectedAgencies.join(', ')}</div>` : ''}
            </div>
          </div>
          
          <div style="margin-top: 12px; text-align: center; display: flex; gap: 8px; justify-content: center;">
            <button onclick="window.dispatchEvent(new CustomEvent('incident-details', { detail: '${incident.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #2196f3, #21cbf3);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(33, 150, 243, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(33, 150, 243, 0.3)';">
              📋 Ver Detalles
            </button>
            ${incident.status === 'ABIERTA' ? `
            <button onclick="window.dispatchEvent(new CustomEvent('incident-assign', { detail: '${incident.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #4caf50, #8bc34a);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(76, 175, 80, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(76, 175, 80, 0.3)';">
              ⚡ Asignar
            </button>` : ''}
          </div>
        </div>
        
        <style>
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.7; }
            100% { opacity: 1; }
          }
        </style>
      `

      if (enablePopups) {
        marker.bindPopup(popupContent, {
          maxWidth: 320,
          closeButton: true,
          autoPan: true,
          keepInView: true,
          className: 'custom-popup'
        })
      }
      
      incidentLayers.value!.addLayer(marker)
    })
  }

  // Filtrar y mostrar supervisores con información avanzada
  const updateSupervisorLayer = (supervisors: Supervisor[]) => {
    if (!supervisorLayers.value) return

    supervisorLayers.value.clearLayers()

    const filteredSupervisors = supervisors.filter(supervisor => {
      const statusMatch = layerFilters.value.supervisors.statuses.length === 0 ||
        layerFilters.value.supervisors.statuses.includes(supervisor.status)
      
      const zoneMatch = layerFilters.value.supervisors.zones.length === 0 ||
        layerFilters.value.supervisors.zones.includes(supervisor.assignedZone)

      // P13-V5A-R5 · "sin posición" es una ausencia, no un cero: el chequeo truthy descartaba
      // también la coordenada 0, que es una posición real. Se comprueba presencia y finitud.
      return statusMatch && zoneMatch &&
        supervisor.currentLatitude != null && Number.isFinite(supervisor.currentLatitude) &&
        supervisor.currentLongitude != null && Number.isFinite(supervisor.currentLongitude)
    })

    filteredSupervisors.forEach(supervisor => {
      // Validar que las coordenadas existan y sean válidas.
      // No se ubica a nadie sin posición medida: mapService ya no fabrica coordenadas para los
      // supervisores que /api/users devuelve sin ellas, así que esta omisión es la forma en que el
      // producto expresa "posición desconocida". No se sustituye por ningún punto por defecto.
      const supervisorLat = supervisor.currentLatitude
      const supervisorLng = supervisor.currentLongitude
      if (supervisorLat == null || supervisorLng == null ||
          !Number.isFinite(supervisorLat) || !Number.isFinite(supervisorLng)) {
        console.warn('⚠️ Supervisor sin coordenadas válidas:', supervisor.id, supervisor.name)
        return
      }

      const icon = createCustomIcon(
        SUPERVISOR_ICONS[supervisor.status],
        SUPERVISOR_COLORS[supervisor.status],
        supervisor.activeIncidents.length > 3 ? 'large' : 'medium'
      )

  const marker = new L!.Marker([supervisorLat, supervisorLng], { icon, interactive: enablePopups })
      
      // Calcular métricas del supervisor
      const workloadLevel = supervisor.activeIncidents.length > 5 ? 'Alto' :
                           supervisor.activeIncidents.length > 2 ? 'Medio' : 'Bajo'
      const workloadColor = supervisor.activeIncidents.length > 5 ? '#f44336' :
                           supervisor.activeIncidents.length > 2 ? '#ff9800' : '#4caf50'
      
      // P13-V5A-R5 · la insignia de frescura sólo se calcula si hay una marca de tiempo medida.
      // mapService ya no sella `lastUpdate` con la hora actual cuando el backend no la trae, y sin
      // marca ninguna de las tres etiquetas es cierta: "🟢 Reciente" afirmaba una frescura que nadie
      // midió y "🔴 Desactualizado" afirmaría una antigüedad igual de inventada. Ausencia declarada.
      const lastUpdateTime = supervisor.lastUpdate ? new Date(supervisor.lastUpdate).getTime() : Number.NaN
      const hasLastUpdate = Number.isFinite(lastUpdateTime)
      const lastUpdateMinutes = Math.floor((Date.now() - lastUpdateTime) / (1000 * 60))
      const updateStatus = !hasLastUpdate ? '' :
                          lastUpdateMinutes < 5 ? '🟢 Reciente' :
                          lastUpdateMinutes < 15 ? '🟡 Normal' : '🔴 Desactualizado'
      
      const popupContent = `
        <div style="min-width: 300px; font-family: 'Roboto', sans-serif;">
          <div style="
            background: linear-gradient(135deg, ${SUPERVISOR_COLORS[supervisor.status]}, ${SUPERVISOR_COLORS[supervisor.status]}dd);
            color: white;
            padding: 8px;
            margin: -8px -8px 8px -8px;
            border-radius: 8px 8px 0 0;
          ">
            <h4 style="margin: 0; font-size: 1rem; display: flex; align-items: center;">
              <span style="margin-right: 6px; font-size: 1.1rem;">${iconToEmoji(SUPERVISOR_ICONS[supervisor.status])}</span>
              ${supervisor.name}
            </h4>
            <div style="display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap;">
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold;">
                ${supervisor.status}
              </span>
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                Zona ${supervisor.assignedZone}
              </span>
              <span style="background: ${workloadColor}; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold;">
                Carga ${workloadLevel}
              </span>
            </div>
          </div>
          
          <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; margin: 8px 0;">
            <div style="display: grid; grid-template-columns: 1fr; gap: 6px; font-size: 11px;">
              <div>
                <strong>📧 Email:</strong><br>
                <a href="mailto:${supervisor.email}" style="color: #2196f3; text-decoration: none;">${supervisor.email}</a>
              </div>
              <div>
                <strong>📞 Teléfono:</strong><br>
                <a href="tel:${supervisor.phone}" style="color: #2196f3; text-decoration: none;">${supervisor.phone}</a>
              </div>
              <div>
                <strong>🎯 Incidencias Activas:</strong><br>
                <span style="
                  background: ${workloadColor}; 
                  color: white; 
                  padding: 2px 8px; 
                  border-radius: 12px; 
                  font-weight: bold;
                ">
                  ${supervisor.activeIncidents.length}
                </span>
              </div>
              <div>
                <strong>🕒 Última Actualización:</strong><br>
                <span style="font-size: 11px;">
                  ${hasLastUpdate
                    ? `${updateStatus}<br>${new Date(supervisor.lastUpdate).toLocaleString('es-DO')}`
                    : '<span style="color: #999;">Sin datos</span>'}
                </span>
              </div>
              <!--
                P13-V5A-R5 · RETIRADO. "Última Agencia Visitada" no mostraba ninguna visita: el
                nombre de la agencia se componía con la zona asignada del supervisor más un dígito
                generado al azar en el cliente, y la fecha era su marca de última actualización
                retrocedida un número aleatorio de horas, bajo un comentario que decía simular una
                visita reciente. No existe productor: este popup se alimenta de
                mapService.getSupervisors(), que mapea filas de GET /api/users, y ese payload no
                contiene visitas de ningún tipo. Un dato sin productor se declara ausente, no se
                rellena; qué debe mostrar aquí un mapa de tracking es una decisión de producto de
                ese vertical, no de esta retirada.
              -->
              <div>
                <strong>🏢 Última Agencia Visitada:</strong><br>
                <span style="color: #999; font-size: 11px;">Sin datos</span>
              </div>
            </div>
          </div>
          
          ${supervisor.activeIncidents.length > 0 ? `
          <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 6px; padding: 10px; margin: 12px 0;">
            <strong style="color: #856404;">🚨 Incidencias Asignadas:</strong>
            <div style="margin-top: 6px; max-height: 80px; overflow-y: auto;">
              ${supervisor.activeIncidents.slice(0, 3).map(incidentId => `
                <div style="
                  background: white; 
                  border-left: 3px solid #ff9800; 
                  padding: 4px 8px; 
                  margin: 4px 0; 
                  border-radius: 0 4px 4px 0; 
                  font-size: 11px;
                  cursor: pointer;
                " onclick="window.dispatchEvent(new CustomEvent('incident-details', { detail: '${incidentId}' }))">
                  📋 ${incidentId}
                </div>
              `).join('')}
              ${supervisor.activeIncidents.length > 3 ? `
                <div style="text-align: center; margin-top: 6px; font-size: 11px; color: #666;">
                  ... y ${supervisor.activeIncidents.length - 3} más
                </div>
              ` : ''}
            </div>
          </div>
          ` : `
          <div style="background: #d4edda; border: 1px solid #c3e6cb; border-radius: 6px; padding: 10px; margin: 12px 0; text-align: center;">
            <span style="color: #155724; font-size: 12px;">
              ✅ <strong>Disponible</strong> - Sin incidencias asignadas
            </span>
          </div>
          `}
          
          <div style="margin-top: 12px; text-align: center; display: flex; gap: 8px; justify-content: center;">
            <button onclick="window.dispatchEvent(new CustomEvent('supervisor-details', { detail: '${supervisor.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #2196f3, #21cbf3);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(33, 150, 243, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(33, 150, 243, 0.3)';">
              👤 Ver Perfil
            </button>
            ${supervisor.status === 'DISPONIBLE' ? `
            <button onclick="window.dispatchEvent(new CustomEvent('supervisor-assign', { detail: '${supervisor.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #4caf50, #8bc34a);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(76, 175, 80, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(76, 175, 80, 0.3)';">
              ⚡ Asignar Tarea
            </button>` : ''}
          </div>
        </div>
      `

      if (enablePopups) {
        marker.bindPopup(popupContent, {
          maxWidth: 350,
          className: 'custom-popup'
        })
      }
      
      supervisorLayers.value!.addLayer(marker)
    })
  }

  // Filtrar y mostrar agencias con clustering por zona y métricas
  const updateAgencyLayer = (agencies: Agency[]) => {
    if (!agencyLayers.value) return

    agencyLayers.value.clearLayers()

    const filteredAgencies = agencies.filter(agency => {
      const statusMatch = layerFilters.value.agencies.statuses.length === 0 ||
        layerFilters.value.agencies.statuses.includes(agency.status)
      
      const zoneMatch = layerFilters.value.agencies.zones.length === 0 ||
        layerFilters.value.agencies.zones.includes(agency.zone)

      return statusMatch && zoneMatch
    })

    // Agrupar agencias por zona para clustering temático
    const agenciesByZone = filteredAgencies.reduce((acc, agency) => {
      if (!acc[agency.zone]) acc[agency.zone] = []
      acc[agency.zone].push(agency)
      return acc
    }, {} as Record<string, Agency[]>)

    filteredAgencies.forEach(agency => {
      // Validar que las coordenadas existan y sean válidas
      if (!agency.latitude || !agency.longitude || 
          isNaN(agency.latitude) || isNaN(agency.longitude)) {
        console.warn('⚠️ Agencia sin coordenadas válidas:', agency.id, agency.name)
        return
      }

      const icon = createCustomIcon(
        AGENCY_ICONS[agency.status],
        AGENCY_COLORS[agency.status],
        agency.incidentCount > 10 ? 'large' : 'medium'
      )

  const marker = new L!.Marker([agency.latitude, agency.longitude], { icon, interactive: enablePopups })
      
      // Calcular métricas de rendimiento
      const zoneAgencies = agenciesByZone[agency.zone] || []
      const avgIncidentsInZone = zoneAgencies.reduce((sum, a) => sum + a.incidentCount, 0) / zoneAgencies.length
      const performanceLevel = agency.incidentCount > avgIncidentsInZone * 1.5 ? 'Alto' :
                              agency.incidentCount > avgIncidentsInZone * 0.8 ? 'Normal' : 'Bajo'
      const performanceColor = agency.incidentCount > avgIncidentsInZone * 1.5 ? '#f44336' :
                              agency.incidentCount > avgIncidentsInZone * 0.8 ? '#ff9800' : '#4caf50'
      
      const lastIncidentDays = agency.lastIncident ? 
        Math.floor((Date.now() - new Date(agency.lastIncident).getTime()) / (1000 * 60 * 60 * 24)) : null
      
      const activityStatus = !agency.lastIncident ? '⚪ Sin incidencias' :
                            lastIncidentDays! < 1 ? '🔴 Incidencia reciente' :
                            lastIncidentDays! < 7 ? '🟡 Actividad normal' : '🟢 Zona tranquila'
      
      const popupContent = `
  <div class="mgi-popup ${isCompactPopup ? 'mgi-popup--compact' : ''}" style="font-family: 'Roboto', sans-serif;">
          <div style="
            background: linear-gradient(135deg, ${AGENCY_COLORS[agency.status]}, ${AGENCY_COLORS[agency.status]}dd);
            color: white;
            padding: 8px;
            margin: -8px -8px 8px -8px;
            border-radius: 8px 8px 0 0;
          ">
            <h4 style="margin: 0; font-size: 1rem; display: flex; align-items: center;">
              <span style="margin-right: 6px; font-size: 1.1rem;">${iconToEmoji(AGENCY_ICONS[agency.status])}</span>
              ${agency.name}
            </h4>
            <div style="display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap;">
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold;">
                ${agency.status}
              </span>
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                Código ${agency.code}
              </span>
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                Zona ${agency.zone}
              </span>
            </div>
          </div>
          
          <div style="margin: 8px 0;">
            <p style="margin: 6px 0; color: #555; font-size: 12px; line-height: 1.35;">
              <strong>📍 Dirección:</strong><br>
              ${agency.address}
            </p>
          </div>
          
          <div style="background: #f8f9fa; padding: 8px; border-radius: 6px; margin: 8px 0;">
            <div style="display: grid; grid-template-columns: 1fr; gap: 6px; font-size: 11px;">
              <div>
                <strong>📞 Teléfono:</strong><br>
                ${agency.contactInfo.phone ? `<a href="tel:${agency.contactInfo.phone}" style="color: #2196f3; text-decoration: none;">${agency.contactInfo.phone}</a>` : '<span style="color: #999;">No disponible</span>'}
              </div>
              <div>
                <strong>📧 Email:</strong><br>
                ${agency.contactInfo.email ? `<a href="mailto:${agency.contactInfo.email}" style="color: #2196f3; text-decoration: none; font-size: 11px;">${agency.contactInfo.email}</a>` : '<span style="color: #999;">No disponible</span>'}
              </div>
              <div>
                <strong>👤 Encargado:</strong><br>
                <span style="font-weight: 500;">${agency.contactInfo.manager || 'No asignado'}</span>
              </div>
              <!--
                P13-V5A-R5 · RETIRADO. "Última Visita" nunca fue una visita: se tomaba la fecha de
                la última INCIDENCIA de la agencia y se retrocedía un número aleatorio de días
                generado en el cliente, bajo un comentario que decía simular una visita reciente.
                Son dos hechos distintos, y el segundo no tiene productor: las agencias del mapa se
                construyen en mapService.getAgencies() a partir de GET /api/points, cuyo payload no
                publica visitas. La rama contraria tampoco era honesta: "Nunca visitada" afirmaba
                una ausencia de visitas que nadie comprobó. La fecha real de la última incidencia
                sigue publicándose más abajo, en "Última Incidencia", que sí tiene origen medido.
              -->
              <div>
                <strong>🚨 Última Visita:</strong><br>
                <span style="color: #999; font-weight: 500;">Sin datos</span>
              </div>
              <div>
                <strong>🎯 Nivel de Actividad:</strong><br>
                <span style="
                  background: ${performanceColor}; 
                  color: white; 
                  padding: 2px 8px; 
                  border-radius: 12px; 
                  font-weight: bold;
                  font-size: 11px;
                ">
                  ${performanceLevel}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Métricas de Rendimiento -->
          <div style="background: linear-gradient(45deg, #e3f2fd, #f3e5f5); border-radius: 6px; padding: 8px; margin: 8px 0;">
            <strong style="color: #1976d2; font-size: 13px;">📊 Métricas de Rendimiento</strong>
            <div style="display: grid; grid-template-columns: 1fr; gap: 6px; margin-top: 6px; font-size: 11px;">
              <div>
                <span style="color: #666;">Total Incidencias:</span><br>
                <span style="
                  background: ${performanceColor}; 
                  color: white; 
                  padding: 2px 8px; 
                  border-radius: 12px; 
                  font-weight: bold;
                ">
                  ${agency.incidentCount}
                </span>
              </div>
              <div>
                <span style="color: #666;">Estado Actual:</span><br>
                <span style="font-size: 11px;">${activityStatus}</span>
              </div>
              <div>
                <span style="color: #666;">Promedio Zona:</span><br>
                <span style="font-weight: bold;">${avgIncidentsInZone.toFixed(1)}</span>
              </div>
              <div>
                <span style="color: #666;">Última Incidencia:</span><br>
                <span style="font-size: 11px;">
                  ${agency.lastIncident ? `Hace ${lastIncidentDays} días` : 'Sin registros'}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Comparativa Zonal -->
          <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 6px; padding: 10px; margin: 12px 0;">
            <strong style="color: #856404; font-size: 12px;">🏘️ Contexto Zonal - ${agency.zone}</strong>
            <div style="margin-top: 6px; font-size: 11px; color: #856404;">
              <div>Total de agencias en zona: <strong>${zoneAgencies.length}</strong></div>
              <div>Incidencias promedio: <strong>${avgIncidentsInZone.toFixed(1)}</strong></div>
              <div>Ranking en zona: <strong>${zoneAgencies.sort((a, b) => b.incidentCount - a.incidentCount).findIndex(a => a.id === agency.id) + 1}° de ${zoneAgencies.length}</strong></div>
            </div>
          </div>
          
          <div style="margin-top: 12px; text-align: center; display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
            <button onclick="window.dispatchEvent(new CustomEvent('agency-details', { detail: '${agency.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #2196f3, #21cbf3);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(33, 150, 243, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(33, 150, 243, 0.3)';">
              🏢 Ver Detalles
            </button>
            <button onclick="window.dispatchEvent(new CustomEvent('agency-incidents', { detail: '${agency.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #ff9800, #ffb74d);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(255, 152, 0, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(255, 152, 0, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(255, 152, 0, 0.3)';">
              📋 Ver Incidencias
            </button>
          </div>
        </div>
      `

      if (enablePopups) {
        marker.bindPopup(popupContent, {
          maxWidth: 320,
          closeButton: true,
          autoPan: true,
          keepInView: true,
          className: 'custom-popup'
        })
      }
      
      agencyLayers.value!.addLayer(marker)
    })
  }

  // Filtrar y mostrar rutas con polilíneas optimizadas y métricas de tiempo
  const updateRouteLayer = (routes: Route[]) => {
    if (!routeLayers.value) return

    routeLayers.value.clearLayers()

    const filteredRoutes = routes.filter(route => {
      const statusMatch = layerFilters.value.routes.statuses.length === 0 ||
        layerFilters.value.routes.statuses.includes(route.status)

      return statusMatch
    })

    filteredRoutes.forEach(route => {
      if (route.coordinates.length === 0) return

      // Configurar estilo de línea según el estado
      const routeStyle = ROUTE_STYLES[route.status]
  const routeLine = new L!.Polyline(route.coordinates as any, {
        color: ROUTE_COLORS[route.status],
        weight: routeStyle.weight,
        opacity: routeStyle.opacity,
        dashArray: routeStyle.dashArray,
        lineCap: 'round',
    lineJoin: 'round',
    interactive: enablePopups
      })

      // Calcular métricas de la ruta
      const completedCheckpoints = route.checkpoints.filter(cp => cp.status === 'VISITADO').length
      const totalCheckpoints = route.checkpoints.length
      const progressPercentage = (completedCheckpoints / totalCheckpoints) * 100
      
      const estimatedHours = Math.floor(route.estimatedDuration / 60)
      const estimatedMinutes = route.estimatedDuration % 60
      const actualHours = route.actualDuration ? Math.floor(route.actualDuration / 60) : 0
      const actualMinutes = route.actualDuration ? route.actualDuration % 60 : 0
      
      const efficiency = route.actualDuration ? 
        ((route.estimatedDuration / route.actualDuration) * 100).toFixed(1) : 'N/A'
      const efficiencyColor = route.actualDuration ?
        (route.actualDuration <= route.estimatedDuration ? '#4caf50' : '#f44336') : '#9e9e9e'
      
      const routePopupContent = `
  <div style="font-family: 'Roboto', sans-serif;">
          <div style="
            background: linear-gradient(135deg, ${ROUTE_COLORS[route.status]}, ${ROUTE_COLORS[route.status]}dd);
            color: white;
            padding: 12px;
            margin: -12px -12px 12px -12px;
            border-radius: 8px 8px 0 0;
          ">
            <h4 style="margin: 0; font-size: 1.1rem; display: flex; align-items: center;">
              <span style="margin-right: 8px; font-size: 1.2rem;">${iconToEmoji('routes')}</span>
              ${route.name}
            </h4>
            <div style="display: flex; gap: 8px; margin-top: 8px; flex-wrap: wrap;">
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: bold;">
                ${route.status}
              </span>
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                ${completedCheckpoints}/${totalCheckpoints} checkpoints
              </span>
              <span style="background: rgba(255,255,255,0.2); padding: 2px 8px; border-radius: 12px; font-size: 11px;">
                ${progressPercentage.toFixed(0)}% completado
              </span>
            </div>
          </div>
          
          <div style="margin: 12px 0;">
            <p style="margin: 8px 0; color: #555; font-size: 13px; line-height: 1.4;">
              <strong>Descripción:</strong><br>
              ${route.description}
            </p>
          </div>
          
          <!-- Barra de progreso -->
          <div style="background: #f0f0f0; border-radius: 20px; height: 8px; margin: 12px 0; overflow: hidden;">
            <div style="
              background: linear-gradient(45deg, ${ROUTE_COLORS[route.status]}, ${ROUTE_COLORS[route.status]}aa);
              height: 100%;
              width: ${progressPercentage}%;
              border-radius: 20px;
              transition: width 0.3s ease;
            "></div>
          </div>
          
          <div style="background: #f8f9fa; padding: 12px; border-radius: 6px; margin: 12px 0;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; font-size: 12px;">
              <div>
                <strong>👤 Supervisor Asignado:</strong><br>
                <span style="font-weight: 500;">${route.supervisorId}</span>
              </div>
              <div>
                <strong>📅 Fecha de Creación:</strong><br>
                <span>${new Date(route.createdAt).toLocaleDateString('es-DO')}</span>
              </div>
              <div>
                <strong>⏱️ Tiempo Estimado:</strong><br>
                <span style="color: #2196f3; font-weight: bold;">
                  ${estimatedHours}h ${estimatedMinutes}m
                </span>
              </div>
              <div>
                <strong>⏰ Tiempo Real:</strong><br>
                <span style="color: ${efficiencyColor}; font-weight: bold;">
                  ${route.actualDuration ? `${actualHours}h ${actualMinutes}m` : 'En progreso'}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Métricas de Eficiencia -->
          ${route.actualDuration ? `
          <div style="background: linear-gradient(45deg, #e8f5e8, #f0f8ff); border-radius: 6px; padding: 12px; margin: 12px 0;">
            <strong style="color: #1976d2; font-size: 13px;">⚡ Análisis de Eficiencia</strong>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 8px; font-size: 12px;">
              <div>
                <span style="color: #666;">Eficiencia:</span><br>
                <span style="
                  background: ${efficiencyColor}; 
                  color: white; 
                  padding: 2px 8px; 
                  border-radius: 12px; 
                  font-weight: bold;
                ">
                  ${efficiency}%
                </span>
              </div>
              <div>
                <span style="color: #666;">Desviación:</span><br>
                <span style="font-weight: bold; color: ${efficiencyColor};">
                  ${route.actualDuration > route.estimatedDuration ? '+' : ''}${route.actualDuration - route.estimatedDuration} min
                </span>
              </div>
            </div>
          </div>
          ` : ''}
          
          <div style="margin-top: 12px; text-align: center; display: flex; gap: 8px; justify-content: center; flex-wrap: wrap;">
            <button onclick="window.dispatchEvent(new CustomEvent('route-details', { detail: '${route.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #2196f3, #21cbf3);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(33, 150, 243, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(33, 150, 243, 0.3)';">
              🛣️ Ver Ruta Completa
            </button>
            ${route.status === 'ACTIVA' ? `
            <button onclick="window.dispatchEvent(new CustomEvent('route-optimize', { detail: '${route.id}' }))" 
                    style="
                      background: linear-gradient(45deg, #4caf50, #8bc34a);
                      color: white;
                      border: none;
                      padding: 8px 16px;
                      border-radius: 20px;
                      cursor: pointer;
                      font-size: 12px;
                      font-weight: bold;
                      transition: all 0.3s ease;
                      box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
                    "
                    onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 4px 8px rgba(76, 175, 80, 0.4)';"
                    onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(76, 175, 80, 0.3)';">
              ⚡ Optimizar
            </button>` : ''}
          </div>
        </div>
      `

      if (enablePopups) {
        routeLine.bindPopup(routePopupContent, {
          maxWidth: 320,
          closeButton: true,
          autoPan: true,
          keepInView: true,
          className: 'custom-popup'
        })
      }
      routeLayers.value!.addLayer(routeLine)

      // Agregar marcadores de checkpoints con diferentes estilos
      route.checkpoints.forEach((checkpoint, index) => {
        const isFirst = index === 0
        const isLast = index === route.checkpoints.length - 1
        
        let checkpointIcon: string
        let checkpointColor: string
        
        if (isFirst) {
          checkpointIcon = 'flag-checkered'
          checkpointColor = '#2196f3'
        } else if (isLast) {
          checkpointIcon = 'flag'
          checkpointColor = '#f44336'
        } else {
          checkpointIcon = checkpoint.status === 'VISITADO' ? 'check-circle' : 
                           checkpoint.status === 'OMITIDO' ? 'close-circle' : 'map-marker'
          checkpointColor = checkpoint.status === 'VISITADO' ? '#4CAF50' : 
                           checkpoint.status === 'OMITIDO' ? '#f44336' : '#ff9800'
        }

  const checkpointMarker = new L!.Marker([checkpoint.latitude, checkpoint.longitude], { 
          icon: createCustomIcon(checkpointIcon, checkpointColor, 'small'),
          interactive: enablePopups
        })

        const checkpointPopupContent = `
          <div class="mgi-popup ${isCompactPopup ? 'mgi-popup--compact' : ''}" style="font-family: 'Roboto', sans-serif;">
            <div style="
              background: linear-gradient(135deg, ${checkpointColor}, ${checkpointColor}dd);
              color: white;
              padding: 10px;
              margin: -12px -12px 10px -12px;
              border-radius: 8px 8px 0 0;
            ">
              <h5 style="margin: 0; font-size: 1rem; display: flex; align-items: center;">
                <span style="margin-right: 6px;">${iconToEmoji(checkpointIcon)}</span>
                ${checkpoint.name}
              </h5>
              <div style="margin-top: 6px;">
                <span style="background: rgba(255,255,255,0.2); padding: 1px 6px; border-radius: 10px; font-size: 10px; font-weight: bold;">
                  ${isFirst ? '🏁 INICIO' : isLast ? '🏁 FINAL' : `#${index + 1}`}
                </span>
              </div>
            </div>
            
            <div style="padding: 8px 0;">
              <div style="font-size: 12px; color: #666;">
                <div style="margin-bottom: 6px;">
                  <strong>Estado:</strong>
                  <span style="
                    background: ${checkpointColor}; 
                    color: white; 
                    padding: 1px 6px; 
                    border-radius: 8px; 
                    font-size: 10px; 
                    margin-left: 4px;
                  ">
                    ${checkpoint.status}
                  </span>
                </div>
                ${checkpoint.visitedAt ? `
                <div style="font-size: 11px; color: #888;">
                  <strong>Visitado:</strong> ${new Date(checkpoint.visitedAt).toLocaleString('es-DO')}
                </div>
                ` : ''}
              </div>
            </div>
            
            <div style="text-align: center; margin-top: 8px;">
              <button onclick="window.dispatchEvent(new CustomEvent('checkpoint-details', { detail: '${checkpoint.id}' }))" 
                      style="
                        background: ${checkpointColor};
                        color: white;
                        border: none;
                        padding: 6px 12px;
                        border-radius: 15px;
                        cursor: pointer;
                        font-size: 11px;
                        font-weight: bold;
                        transition: all 0.3s ease;
                      "
                      onmouseover="this.style.opacity='0.8';"
                      onmouseout="this.style.opacity='1';">
                📍 Ver Detalles
              </button>
            </div>
          </div>
        `

        if (enablePopups) {
          checkpointMarker.bindPopup(checkpointPopupContent, {
            maxWidth: 280,
            closeButton: true,
            autoPan: true,
            keepInView: true,
            className: 'custom-popup'
          })
        }
        routeLayers.value!.addLayer(checkpointMarker)
      })
    })
  }

  // Métodos de control de visibilidad
  const toggleLayerVisibility = (layerType: keyof LayerVisibility) => {
    layerVisibility.value[layerType] = !layerVisibility.value[layerType]
    
    if (!map) return

    const layer = getLayerByType(layerType)
    if (!layer) return

    if (layerVisibility.value[layerType]) {
      map.addLayer(layer)
    } else {
      map.removeLayer(layer)
    }
  }

  const setLayerVisibility = (layerType: keyof LayerVisibility, visible: boolean) => {
    console.log('🔧 setLayerVisibility:', layerType, 'visible:', visible)
    layerVisibility.value[layerType] = visible
    
    if (!map) {
      console.warn('⚠️ Mapa no disponible para setLayerVisibility')
      return
    }

    // Manejar clustering por separado (no es una capa física)
    if (layerType === 'clustering') {
      console.log('🔧 Clustering toggle:', visible ? 'activado' : 'desactivado')
      // Aquí podrías agregar lógica específica de clustering si es necesario
      return
    }

    const layer = getLayerByType(layerType)
    if (!layer) {
      console.warn('⚠️ Capa no encontrada:', layerType)
      return
    }

    if (visible) {
      // Forzar agregar la capa (Leaflet maneja duplicados automáticamente)
      try {
        map.addLayer(layer)
        console.log('✅ Capa agregada al mapa:', layerType)
      } catch (error) {
        console.log('ℹ️ Capa ya estaba en el mapa:', layerType)
      }
    } else {
      // Forzar remover la capa
      try {
        map.removeLayer(layer)
        console.log('✅ Capa removida del mapa:', layerType)
      } catch (error) {
        console.log('ℹ️ Capa ya estaba removida:', layerType)
      }
    }
  }

  const getLayerByType = (layerType: keyof LayerVisibility) => {
    switch (layerType) {
      case 'incidents':
        return incidentLayers.value
      case 'supervisors':
        return supervisorLayers.value
      case 'agencies':
        return agencyLayers.value
      case 'routes':
        return routeLayers.value
      default:
        return null
    }
  }

  // Métodos de filtrado
  const updateLayerFilters = (newFilters: Partial<LayerFilters>) => {
    layerFilters.value = { ...layerFilters.value, ...newFilters }
  }

  const resetLayerFilters = () => {
    layerFilters.value = {
      incidents: {
        priorities: ['ALTA', 'CRITICA'],
        statuses: ['ABIERTA', 'EN_PROGRESO'],
        categories: []
      },
      supervisors: {
        statuses: ['ACTIVO', 'EN_RUTA'],
        zones: []
      },
      agencies: {
        statuses: ['ACTIVA'],
        zones: []
      },
      routes: {
        statuses: ['ACTIVA']
      }
    }
  }

  // Computed properties
  const layerCounts = computed(() => ({
    incidents: incidentLayers.value?.getLayers?.()?.length || 0,
    supervisors: supervisorLayers.value?.getLayers?.()?.length || 0,
    agencies: agencyLayers.value?.getLayers?.()?.length || 0,
    routes: routeLayers.value?.getLayers?.()?.length || 0
  }))

  const activeLayersCount = computed(() => {
    return Object.values(layerVisibility.value).filter(Boolean).length
  })

  // Watchers para manejar cambios en clustering
  watch(() => layerVisibility.value.clustering, async (newClustering, oldClustering) => {
    console.log('🔧 Clustering cambió de', oldClustering, 'a', newClustering)
    
    // Solo afectar la capa de incidencias que es la que usa clustering
    if (newClustering !== oldClustering && incidentLayers.value) {
      console.log('📦 Actualizando capa de incidencias para clustering:', newClustering ? 'activado' : 'desactivado')
      
      // Remover la capa actual del mapa
      if (map && map.hasLayer(incidentLayers.value)) {
        map.removeLayer(incidentLayers.value)
      }
      
      // Guardar marcadores actuales para migrarlos al nuevo grupo
      const existingMarkers = (incidentLayers.value as any)?.getLayers?.() ?? []

      // Recrear solo la capa de incidencias con el tipo correcto
      if (newClustering) {
        // Asegurar carga del plugin y CSS
        if (!MarkerClusterGroupRef) {
          try {
            await ensureMarkerClusterLoaded()
          } catch (e) {
            console.warn('⚠️ No se pudo cargar leaflet.markercluster, usando LayerGroup temporal', e)
          }
        }
        if (MarkerClusterGroupRef) {
          incidentLayers.value = new MarkerClusterGroupRef({
            chunkedLoading: true,
            maxClusterRadius: 50,
            spiderfyOnMaxZoom: true,
            showCoverageOnHover: false,
            zoomToBoundsOnClick: true,
            iconCreateFunction: (cluster: any) => {
              const count = cluster.getChildCount()
              let size = 'medium'
              let color = '#2196F3'
              if (count > 20) { size = 'large'; color = '#F44336' }
              else if (count > 10) { size = 'medium'; color = '#FF9800' }
              return new L!.DivIcon({
                html: `
                  <div style="
                    background-color: ${color};
                    border-radius: 50%;
                    width: ${size === 'large' ? '50px' : '40px'};
                    height: ${size === 'large' ? '50px' : '40px'};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 3px 8px rgba(0,0,0,0.4);
                    font-weight: bold;
                    color: white;
                    font-size: ${size === 'large' ? '16px' : '14px'};
                  ">
                    ${count}
                  </div>
                `,
                className: 'cluster-icon',
                iconSize: [size === 'large' ? 50 : 40, size === 'large' ? 50 : 40]
              })
            }
          })
        } else {
          // Fallback si el plugin no cargó
          incidentLayers.value = new L!.LayerGroup()
        }
      } else {
        incidentLayers.value = new L!.LayerGroup()
      }

      // Migrar marcadores existentes al nuevo grupo
      try {
        if (existingMarkers.length && (incidentLayers.value as any)?.addLayer) {
          existingMarkers.forEach((m: any) => (incidentLayers.value as any).addLayer(m))
        }
      } catch (e) {
        console.warn('⚠️ Error migrando marcadores al nuevo grupo de incidencias', e)
      }
      
      // Agregar la nueva capa al mapa si incidents está visible
      if (layerVisibility.value.incidents && incidentLayers.value && map) {
        map.addLayer(incidentLayers.value)
      }
      
      console.log('✅ Capa de incidencias actualizada para clustering:', newClustering ? 'con clustering' : 'sin clustering')
      
      // Nota: Los datos se repoblarán automáticamente desde el componente padre
    }
  })

  return {
    // Estados
    layerVisibility,
    layerFilters,
    layerCounts,
    activeLayersCount,

    // Referencias de capas
    incidentLayers,
    supervisorLayers,
    agencyLayers,
    routeLayers,

    // Métodos de inicialización
  initializeLayers,
    clearAllLayers,

    // Métodos de actualización de capas
    updateIncidentLayer,
    updateSupervisorLayer,
    updateAgencyLayer,
    updateRouteLayer,

    // Métodos de control de visibilidad
    toggleLayerVisibility,
    setLayerVisibility,

    // Métodos de filtrado
    updateLayerFilters,
    resetLayerFilters,

    // Utilidades
    getLayerByType
  }
}
